import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Card,
  CardBody,
  Icon,
  Badge,
  Avatar,
  Divider,
  SimpleGrid,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Input,
  FormControl,
  FormLabel,
  Flex,
  Circle,
  Tooltip,
  IconButton,
} from '@chakra-ui/react';
import { User, Buildings, Envelope, Shield, Phone, Plus, Trash } from 'phosphor-react';
import { toast } from 'sonner';
import { api } from '../../../services/api';
import SecaoClienteLayout from '../layout';

interface IEmpresa {
  id: number;
  razao_social: string;
  nome_fantasia: string;
  cnpj: string | null;
}

interface ITelefone {
  id: number;
  telefone: string;
}

interface ClienteData {
  id: number;
  guid: string;
  nome: string;
  email: string;
  empresas?: IEmpresa[];
  telefones?: (string | ITelefone)[];
}

// Helper para extrair string do telefone (pode vir como string ou objeto)
const getTelefoneString = (tel: string | ITelefone | null | undefined): string => {
  if (!tel) return '';
  if (typeof tel === 'string') return tel;
  if (typeof tel === 'object' && 'telefone' in tel) return tel.telefone || '';
  return String(tel);
};

const SecaoClientePerfil = () => {
  const [clienteData, setClienteData] = useState<ClienteData | null>(() => {
    const clienteDataStr = localStorage.getItem('cliente_data');
    return clienteDataStr ? JSON.parse(clienteDataStr) : null;
  });

  // Estados para telefones
  const {
    isOpen: isOpenTelefone,
    onOpen: onOpenTelefone,
    onClose: onCloseTelefone,
  } = useDisclosure();
  const [telefones, setTelefones] = useState<string[]>(() => {
    return (clienteData?.telefones || []).map(getTelefoneString).filter(Boolean);
  });
  const [novoTelefone, setNovoTelefone] = useState('');
  const [isSavingTelefone, setIsSavingTelefone] = useState(false);

  // Buscar dados atualizados da API
  useEffect(() => {
    const fetchClienteData = async () => {
      if (!clienteData?.id) {
        return;
      }

      try {
        const token = localStorage.getItem('cliente_access_token');
        const response = await api.get(`/api/v1/secao-cliente/${clienteData.id}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const dadosAtualizados = response.data.data;

        // Atualizar estado e localStorage
        setClienteData(dadosAtualizados);
        const telefonesNormalizados = (dadosAtualizados.telefones || [])
          .map(getTelefoneString)
          .filter(Boolean);
        setTelefones(telefonesNormalizados);
        localStorage.setItem('cliente_data', JSON.stringify(dadosAtualizados));
      } catch (error) {
        toast.error('Erro ao buscar dados do cliente');
      }
    };

    fetchClienteData();
  }, []);

  // Pegar iniciais do nome
  const getInitials = (nome: string) => {
    const parts = nome.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return nome.slice(0, 2).toUpperCase();
  };

  // Formatar CNPJ: XX.XXX.XXX/XXXX-XX
  const formatarCnpj = (cnpj: string | null) => {
    if (!cnpj) return 'CNPJ não informado';
    const numeros = cnpj.replace(/\D/g, '');
    if (numeros.length !== 14) return cnpj;
    return `${numeros.slice(0, 2)}.${numeros.slice(2, 5)}.${numeros.slice(5, 8)}/${numeros.slice(8, 12)}-${numeros.slice(12)}`;
  };

  const empresas = clienteData?.empresas || [];

  // Formatar telefone
  const formatarTelefone = (valor: string | number | undefined | null) => {
    if (!valor) return '';
    const valorStr = String(valor);
    const numeros = valorStr.replace(/\D/g, '');
    if (numeros.length <= 2) return numeros;
    if (numeros.length <= 6) return `(${numeros.slice(0, 2)}) ${numeros.slice(2)}`;
    if (numeros.length <= 10) {
      return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 6)}-${numeros.slice(6)}`;
    }
    return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 7)}-${numeros.slice(7)}`;
  };

  const handleTelefoneChange = (valor: string) => {
    setNovoTelefone(formatarTelefone(valor));
  };

  const handleAdicionarTelefone = async () => {
    const numeros = novoTelefone.replace(/\D/g, '');
    if (numeros.length < 10) {
      toast.error('Digite um telefone válido');
      return;
    }

    if (!clienteData?.id) {
      toast.error('Dados do cliente não encontrados');
      return;
    }

    setIsSavingTelefone(true);

    try {
      const token = localStorage.getItem('cliente_access_token');
      // Montar lista de telefones com o novo
      const novaListaTelefones = [...telefones, numeros];

      const response = await api.put(
        `/api/v1/secao-cliente/${clienteData.id}/`,
        { telefones: novaListaTelefones },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Atualizar estado com dados retornados pela API
      const dadosAtualizados = response.data.data;
      const telefonesAtualizados = (dadosAtualizados.telefones || [])
        .map(getTelefoneString)
        .filter(Boolean);
      setTelefones(telefonesAtualizados);

      // Atualizar localStorage
      setClienteData(dadosAtualizados);
      localStorage.setItem('cliente_data', JSON.stringify(dadosAtualizados));

      toast.success('Telefone adicionado com sucesso!');
      setNovoTelefone('');
      onCloseTelefone();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail || 'Erro ao adicionar telefone';
      toast.error(errorMessage);
    } finally {
      setIsSavingTelefone(false);
    }
  };

  const handleRemoverTelefone = async (telefoneRemover: string) => {
    if (!clienteData?.id) {
      toast.error('Dados do cliente não encontrados');
      return;
    }

    try {
      const token = localStorage.getItem('cliente_access_token');
      // Remover telefone da lista
      const novaListaTelefones = telefones.filter(t => t !== telefoneRemover);

      const response = await api.put(
        `/api/v1/secao-cliente/${clienteData.id}/`,
        { telefones: novaListaTelefones },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Atualizar estado com dados retornados pela API
      const dadosAtualizados = response.data.data;
      const telefonesAtualizados = (dadosAtualizados.telefones || [])
        .map(getTelefoneString)
        .filter(Boolean);
      setTelefones(telefonesAtualizados);

      // Atualizar localStorage
      setClienteData(dadosAtualizados);
      localStorage.setItem('cliente_data', JSON.stringify(dadosAtualizados));

      toast.success('Telefone removido!');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail || 'Erro ao remover telefone';
      toast.error(errorMessage);
    }
  };

  return (
    <SecaoClienteLayout>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box>
          <HStack spacing={3}>
            <Box p={3} borderRadius="xl" bg="purple.50">
              <Icon as={User} boxSize={6} color="purple.500" weight="duotone" />
            </Box>
            <Box>
              <Heading size="lg" color="gray.800">
                Meu Perfil
              </Heading>
              <Text color="gray.500" fontSize="sm">
                Gerencie suas informações e empresas vinculadas
              </Text>
            </Box>
          </HStack>
        </Box>

        {/* Card de Informações do Usuário */}
        <Card>
          <CardBody>
            <Flex align="center" gap={6} flexWrap="wrap">
              <Avatar
                size="xl"
                name={clienteData?.nome || 'Cliente'}
                bg="purple.500"
                color="white"
                fontSize="2xl"
              >
                {clienteData?.nome ? getInitials(clienteData.nome) : 'CL'}
              </Avatar>

              <Box flex={1}>
                <Heading size="md" color="gray.800" mb={1}>
                  {clienteData?.nome || 'Cliente'}
                </Heading>
                <HStack spacing={2} color="gray.500" mb={3}>
                  <Icon as={Envelope} />
                  <Text fontSize="sm">{clienteData?.email || 'email@exemplo.com'}</Text>
                </HStack>
              </Box>

              <Box textAlign="center" p={4} bg="gray.50" borderRadius="lg">
                <Text fontSize="3xl" fontWeight="bold" color="purple.600">
                  {empresas.length}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  Empresas vinculadas
                </Text>
              </Box>
            </Flex>
          </CardBody>
        </Card>

        {/* Minhas Empresas */}
        {empresas.length > 0 ? (
          <Card>
            <CardBody>
              <HStack mb={4}>
                <Icon as={Buildings} boxSize={5} color="purple.500" />
                <Heading size="sm" color="gray.700">
                  Minhas Empresas ({empresas.length})
                </Heading>
              </HStack>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                {empresas.map(empresa => (
                  <Card key={empresa.id} variant="outline" size="sm">
                    <CardBody py={3}>
                      <Flex align="center" gap={3}>
                        <Circle size="40px" bg="purple.50">
                          <Icon as={Buildings} color="purple.500" boxSize={5} />
                        </Circle>
                        <Box flex={1}>
                          <Text fontSize="sm" fontWeight="600" noOfLines={1}>
                            {empresa.nome_fantasia || empresa.razao_social}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {formatarCnpj(empresa.cnpj)}
                          </Text>
                        </Box>
                      </Flex>
                    </CardBody>
                  </Card>
                ))}
              </SimpleGrid>
            </CardBody>
          </Card>
        ) : (
          <Card variant="outline" bg="gray.50">
            <CardBody>
              <VStack py={8} spacing={4}>
                <Icon as={Buildings} boxSize={12} color="gray.300" />
                <Text color="gray.500" textAlign="center">
                  Nenhuma empresa vinculada à sua conta
                </Text>
              </VStack>
            </CardBody>
          </Card>
        )}

        <Divider />

        {/* Seção de Telefones */}
        <Card>
          <CardBody>
            <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={2}>
              <HStack>
                <Icon as={Phone} boxSize={5} color="teal.500" />
                <Heading size="sm" color="gray.700">
                  Meus Telefones
                </Heading>
              </HStack>
              <Button
                size="sm"
                colorScheme="teal"
                leftIcon={<Plus size={16} />}
                onClick={onOpenTelefone}
              >
                Adicionar Telefone
              </Button>
            </Flex>

            {telefones.length > 0 ? (
              <VStack spacing={3} align="stretch">
                {telefones.map(telefone => (
                  <Card key={telefone} variant="outline" size="sm">
                    <CardBody py={3}>
                      <Flex align="center" gap={3}>
                        <Circle size="40px" bg="teal.50">
                          <Icon as={Phone} color="teal.500" boxSize={5} />
                        </Circle>
                        <Box flex={1}>
                          <Text fontSize="sm" fontWeight="600">
                            {formatarTelefone(telefone) || telefone || 'Telefone inválido'}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            WhatsApp / Fixo
                          </Text>
                        </Box>
                        <Tooltip label="Remover telefone">
                          <IconButton
                            aria-label="Remover telefone"
                            icon={<Trash size={18} />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => handleRemoverTelefone(telefone)}
                          />
                        </Tooltip>
                      </Flex>
                    </CardBody>
                  </Card>
                ))}
              </VStack>
            ) : (
              <Box p={6} bg="gray.50" borderRadius="lg" textAlign="center">
                <Icon as={Phone} boxSize={8} color="gray.300" mb={2} />
                <Text color="gray.500" fontSize="sm">
                  Nenhum telefone cadastrado
                </Text>
                <Text color="gray.400" fontSize="xs">
                  Adicione um telefone para contato
                </Text>
              </Box>
            )}
          </CardBody>
        </Card>

        <Divider />

        {/* Informações de Segurança */}
        <Card>
          <CardBody>
            <HStack mb={4}>
              <Icon as={Shield} boxSize={5} color="blue.500" />
              <Heading size="sm" color="gray.700">
                Segurança da Conta
              </Heading>
            </HStack>
            <Box p={4} bg="gray.50" borderRadius="lg">
              <HStack justify="space-between">
                <Box>
                  <Text fontWeight="600" fontSize="sm">
                    Autenticação
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    Email e senha
                  </Text>
                </Box>
                <Badge colorScheme="green">Ativo</Badge>
              </HStack>
            </Box>
          </CardBody>
        </Card>
      </VStack>

      {/* Modal Adicionar Telefone */}
      <Modal isOpen={isOpenTelefone} onClose={onCloseTelefone}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Adicionar Telefone</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Número do Telefone</FormLabel>
                <Input
                  placeholder="(00) 00000-0000"
                  value={novoTelefone}
                  onChange={e => handleTelefoneChange(e.target.value)}
                />
              </FormControl>

              <HStack w="100%" p={3} bg="gray.50" borderRadius="md">
                <Icon as={Phone} color="teal.500" />
                <Text fontSize="sm" color="gray.600">
                  WhatsApp / Fixo
                </Text>
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCloseTelefone}>
              Cancelar
            </Button>
            <Button
              colorScheme="teal"
              onClick={handleAdicionarTelefone}
              isLoading={isSavingTelefone}
              leftIcon={<Plus size={18} />}
            >
              Adicionar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </SecaoClienteLayout>
  );
};

export default SecaoClientePerfil;
